import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Número em notação compacta: 1.284.530 → "1,3 mi".
 *
 * Saldo de moeda vive em espaço fixo — a dock e a barra de moedas têm
 * a largura da tela, não a do número. Um jogador avançado com sete
 * dígitos empurrava a dock para 421px, ou seja, para fora de qualquer
 * telefone de 360px: os botões das pontas ficavam inalcançáveis.
 *
 * Abaixo de 10 mil mostra o valor exato, que é a faixa em que cada
 * unidade ainda importa para o jogador.
 */
export function formatCompactNumber(value: number, locale = "pt-BR"): string {
  if (!Number.isFinite(value)) return "0";
  const abs = Math.abs(value);
  if (abs < 10000) return Math.round(value).toLocaleString(locale);

  // Sufixo curto em vez do `notation: "compact"` do Intl: em pt-BR ele
  // devolve "12,5 mil" (96px na dock), contra "12,5k" (~38px). O que
  // decide aqui é a largura previsível, não a prosa.
  const units: Array<[number, string]> = [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "k"],
  ];
  for (const [threshold, suffix] of units) {
    if (abs >= threshold) {
      const scaled = value / threshold;
      // Uma casa decimal só enquanto ela informa: 12,5k sim, 125,4k não.
      const digits = Math.abs(scaled) < 100 ? 1 : 0;
      return `${scaled.toLocaleString(locale, { maximumFractionDigits: digits })}${suffix}`;
    }
  }
  return Math.round(value).toLocaleString(locale);
}

const getLocalStorage = (key: string): any =>
  JSON.parse(window.localStorage.getItem(key) || "null");
const setLocalStorage = (key: string, value: any): void =>
  window.localStorage.setItem(key, JSON.stringify(value));

/**
 * Player ID Key - Chave do localStorage para ID do jogador
 */
const PLAYER_ID_KEY = "rocketanks-player-id";

/**
 * Obtém o ID único do jogador.
 * Se não existir, gera um novo UUID e armazena.
 * @returns O ID do jogador (UUID)
 */
export function getPlayerId(): string {
  const stored = localStorage.getItem(PLAYER_ID_KEY);
  if (stored) {
    return stored;
  }
  const newId = uuidv4();
  localStorage.setItem(PLAYER_ID_KEY, newId);
  return newId;
}

/**
 * Define um ID de jogador específico (para migração/debug)
 * @param id - Novo ID do jogador
 */
export function setPlayerId(id: string): void {
  localStorage.setItem(PLAYER_ID_KEY, id);
}

/**
 * Verifica se o jogador tem um ID salvo
 * @returns true se o jogador tem ID
 */
export function hasPlayerId(): boolean {
  return !!localStorage.getItem(PLAYER_ID_KEY);
}

export { getLocalStorage, setLocalStorage };
